import React from "react";
import {
    Box, Button, Card, CardContent, CircularProgress, IconButton, Stack, Tooltip, Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import FactRow from "./FactRow";
import ProjectMenuButton from "./ProjectMenuButton";
import {
    CARD_CONTENT_SX, cardHeaderSx, CARD_NAME_SX, ENTITY_CARD_SELECTED_SX, ENTITY_CARD_SX,
    CARD_ACTIONS_SX, HEADER_DELETE_SX, HEADER_ICON_SX, PRIMARY_BUTTON_SX,
} from "./entityCardChrome";
import { REPRODUCTION_KINDS, reproductionModeLabel } from "../helper/reproductionFlow";
import KindChip from "./KindChip";
import { REPRODUCTION_KIND_ICONS } from "./itemKindStyles";
import { formatDateOnly } from "../helper/formatDate";

/** The REPRODUCTION_KINDS descriptor of a row, with its slug (a row without
 *  a type is a logo). */
export function reproductionKindOf(row) {
    const type = row?.reproduction_type || "LOGO_REPRODUCTION";
    const slug = Object.keys(REPRODUCTION_KINDS).find((key) => REPRODUCTION_KINDS[key].type === type) || "logo";
    return { ...REPRODUCTION_KINDS[slug], slug };
}

/**
 * One reproduction as the overview lists and the Projects page show it.
 *
 * @param row          a row of api/reproduction/get_existing_reproductions
 * @param rights       { allowed, reason } from deleteRights()
 * @param thumbnail    object URL of the rendered preview; "" = failed; undefined = loading
 * @param onOpen       Continue / Open
 * @param onDownload   downloads the input file
 * @param downloading  the download of this card is running
 * @param onDelete     opens the delete dialog
 * @param projects, onMoveToProject, showProject, selected, dragProps  see ExplorationCard
 */
export default function ReproductionCard({
    row, rights, thumbnail, onOpen, onDownload, downloading = false, onDelete,
    projects, onMoveToProject, showProject = true, selected = false, dragProps = {},
}) {
    const kind = reproductionKindOf(row);
    const finished = Boolean(row.setup_final);

    return (
        <Card
            variant="outlined"
            data-kind="reproduction"
            data-id={row.id}
            sx={{ ...ENTITY_CARD_SX, ...(selected ? ENTITY_CARD_SELECTED_SX : {}) }}
            {...dragProps}
        >
            {/* dark header band: name + actions, then the kind on its own row */}
            <Box sx={cardHeaderSx("reproduction")}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography component="div" sx={{ ...CARD_NAME_SX, mt: 0, minWidth: 0 }}>
                        {row.name}
                    </Typography>
                    <Stack direction="row" alignItems="center" sx={{ mr: -0.5, flexShrink: 0 }}>
                        <Tooltip title="Download input file">
                            <span>
                                <IconButton
                                    size="small"
                                    onClick={onDownload}
                                    disabled={downloading}
                                    aria-label={`download input file of ${row.name}`}
                                    sx={{ ...HEADER_ICON_SX, "&.Mui-disabled": { color: "rgba(255,255,255,0.6)" } }}
                                >
                                    {downloading
                                        ? <CircularProgress size={16} sx={{ color: "inherit" }} />
                                        : <DownloadIcon fontSize="small" />}
                                </IconButton>
                            </span>
                        </Tooltip>
                        <ProjectMenuButton
                            projects={projects}
                            currentProjectId={row.project || null}
                            onMove={onMoveToProject}
                            itemLabel="reproduction"
                        />
                        <Tooltip title={rights?.reason || "Delete reproduction"}>
                            {/* span keeps the tooltip working on a disabled button */}
                            <span>
                                <IconButton
                                    size="small"
                                    disabled={!rights?.allowed}
                                    onClick={onDelete}
                                    aria-label="delete reproduction"
                                    sx={HEADER_DELETE_SX}
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Stack>
                </Stack>
                <Box sx={{ mt: 0.75 }}>
                    <KindChip
                        Icon={REPRODUCTION_KIND_ICONS[kind.slug]}
                        label={kind.navLabel}
                        title={`${kind.navLabel} reproduction`}
                    />
                </Box>
            </Box>

            {/* Rendered preview: logo in its palette colors, gradient blend,
                image simulated result - the input until colors are set. */}
            <Box
                sx={{
                    height: 84, bgcolor: "grey.50", borderBottom: "1px solid",
                    borderColor: "rgba(15, 23, 42, 0.06)", display: "flex",
                    alignItems: "center", justifyContent: "center", overflow: "hidden",
                }}
            >
                {thumbnail ? (
                    <img
                        src={thumbnail}
                        alt={`Rendered preview of ${row.name}`}
                        style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", display: "block" }}
                        draggable={false}
                    />
                ) : (
                    <Typography variant="caption" color="text.disabled">
                        {thumbnail === "" ? "No preview" : "Loading preview..."}
                    </Typography>
                )}
            </Box>

            <CardContent sx={CARD_CONTENT_SX}>
                <Stack spacing={0} sx={{ flex: 1 }}>
                    <FactRow dense label={kind.fileLabel} value={row.original_file_name || "—"} />
                    <FactRow dense label="Laser device" value={row.laser_device_name || "—"} />
                    <FactRow dense label="Substrate" value={row.material_name || "—"} />
                    <FactRow dense label="Palette" value={row.palette_selection_name || "—"} />
                    {row.reproduction_mode ? (
                        <FactRow dense label="Mode" value={reproductionModeLabel(row.reproduction_mode)} />
                    ) : null}
                    <FactRow dense label="Created by" value={row.started_by_email || "—"} />
                    <FactRow dense label="Created" value={formatDateOnly(row.created_at)} />
                    {showProject ? (
                        <FactRow dense label="Project" value={row.project_name || "—"} />
                    ) : null}

                    <Stack direction="row" spacing={1} alignItems="center" sx={CARD_ACTIONS_SX}>
                        <Button onClick={onOpen} variant="contained" size="small" disableElevation sx={PRIMARY_BUTTON_SX}>
                            {finished ? "Open" : "Continue"}
                        </Button>
                    </Stack>
                </Stack>
            </CardContent>
        </Card>
    );
}
